/**
 * MediaViewer (06 §4.17 / S20) — full-bleed modal over the conversation. Header
 * (sender + time), image/doc body, footer (download/forward/delete). Esc/back/
 * click-scrim closes. Decryption happens client-side upstream (the blob URL is
 * already decrypted in the view-model).
 */
import { useEffect } from 'react';
import { X, Download, Forward, Trash2, FileText } from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import { clockTime } from '../../lib/format';
import { Avatar } from '../primitives/Avatar';
import styles from './MediaViewer.module.css';

export function MediaViewer() {
  const clientMsgId = useUiStore((s) => s.mediaForMessage);
  const closeMedia = useUiStore((s) => s.closeMedia);
  const toast = useUiStore((s) => s.toast);
  const userById = useChatStore((s) => s.userById);
  const messagesByConv = useChatStore((s) => s.messagesByConv);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMedia();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [closeMedia]);

  if (!clientMsgId) return null;
  const message = Object.values(messagesByConv)
    .flat()
    .find((m) => m.clientMsgId === clientMsgId);
  if (!message || !message.media) return null;

  const sender = userById(message.senderId);

  return (
    <div className={styles.scrim} onClick={closeMedia} role="dialog" aria-modal="true">
      <header className={styles.header} onClick={(e) => e.stopPropagation()}>
        <button className={styles.iconBtn} onClick={closeMedia} aria-label="Close">
          <X size={24} />
        </button>
        <div className={styles.who}>
          <Avatar id={message.senderId} name={sender?.displayName ?? '?'} size={36} />
          <div>
            <div className={styles.name}>{sender?.displayName ?? 'Unknown'}</div>
            <div className={styles.time}>{clockTime(message.sentAtMs)}</div>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={styles.iconBtn} onClick={() => toast('Downloaded')} aria-label="Download">
            <Download size={22} />
          </button>
          <button className={styles.iconBtn} onClick={() => toast('Forwarding…')} aria-label="Forward">
            <Forward size={22} />
          </button>
          <button className={styles.iconBtn} onClick={() => toast('Deleted')} aria-label="Delete">
            <Trash2 size={22} />
          </button>
        </div>
      </header>

      <div className={styles.body} onClick={(e) => e.stopPropagation()}>
        {message.contentType === 'image' ? (
          <img src={message.media.url} alt="" className={styles.image} />
        ) : (
          <div className={styles.doc}>
            <FileText size={64} />
            <span>{message.media.fileName ?? 'Document'}</span>
          </div>
        )}
      </div>
    </div>
  );
}
